<template>
  <header
    v-if="header.title"
    class="main-header-page-hero"
  >
    <div class="main-header-page-hero__bg">
      <div class="bubble bubble-1" />
      <div class="bubble bubble-2" />
      <div class="bubble bubble-3" />
    </div>

    <div class="main-header-page-hero__icon">
      <feather-icon
        :icon="header.icon"
        size="28"
      />
    </div>
    <div class="main-header-page-hero__text">
      <h2>{{ header.title }}</h2>
      <p>{{ header.subtitle }}</p>
    </div>

    <!-- shown only when the entry in main-data-header.js has a `goto` and/or `refresh` key -->
    <div
      v-if="header.goto || header.refresh"
      class="main-header-page-hero__actions"
    >
      <!-- refresh: tells the page to reload its data (the page listens with @refresh) -->
      <button
        v-if="header.refresh"
        type="button"
        class="main-header-page__back"
        :disabled="refreshing"
        @click="$emit('refresh')"
      >
        <feather-icon
          icon="RefreshCwIcon"
          size="16"
          :class="{ 'main-header-page__spin': refreshing }"
        />
        {{ header.refreshLabel }}
      </button>

      <button
        v-if="header.goto"
        type="button"
        class="main-header-page__back"
        @click="goTo"
      >
        <feather-icon
          :icon="header.gotoIcon"
          size="16"
        />
        {{ header.gotoLabel }}
      </button>
    </div>

    <!-- shown only when the entry in main-data-header.js has a non-empty `stats` array -->
    <div
      v-if="header.stats.length"
      class="stats-row"
    >
      <div
        v-for="(stat, index) in header.stats"
        :key="`${stat.label}-${index}`"
        class="stat-card"
      >
        <div class="stat-card__icon">
          <feather-icon
            :icon="stat.icon || 'ActivityIcon'"
            size="20"
          />
        </div>
        <div class="stat-card__body">
          <span class="stat-card__label">{{ stat.label }}</span>
          <span class="stat-card__value">{{ stat.value }}</span>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { computed } from '@vue/composition-api'
import { useRouter } from '@core/utils/utils'
import mainData from './main-data-header'

const { mainDataHeader } = mainData

// matches https://…, http://…, //cdn…, mailto:…, tel:…
const EXTERNAL_LINK = /^(https?:)?\/\/|^(mailto|tel):/i

// 'requests-analysis' -> 'requestsAnalysis'
const toCamelCase = text => text.replace(/-([a-z0-9])/g, (_, char) => char.toUpperCase())

/**
 * Returns the header data stored under `key` in main-data-header.js
 * (for example "analysis"), or null when the key does not exist.
 * Route names are usually kebab-case ('requests-analysis') while the data keys may be written
 * camelCase (requestsAnalysis), so both spellings are tried.
 */
export const getHeaderData = key => {
  if (!key) return null
  return mainDataHeader[key] || mainDataHeader[toCamelCase(String(key))] || null
}

export default {
  // The header is chosen by comparing the current ROUTE NAME with the keys of mainDataHeader.
  // Same name -> that data is shown, no match -> nothing is rendered.
  props: {
    // Live values for the stat cards, matched by each stat's `key` in main-data-header:
    //   <MainHeader :stats-values="{ totalRows, totalActiveLoad }" />
    // A card with no matching key keeps the static `value` from its data file.
    statsValues: {
      type: Object,
      default: () => ({}),
    },
    // true while the page is reloading: the refresh icon spins and the button is disabled
    refreshing: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { router, route } = useRouter()

    const header = computed(() => {
      const data = getHeaderData(route.value.name) || {}

      return {
        icon: data.icon || 'PieChartIcon',
        title: data.title || 'Gulftic',
        subtitle: data.subtitle || 'Gulftic System Dashboard',
        goto: data.goto || null,
        gotoLabel: data.gotoLabel || 'Back',
        gotoIcon: data.gotoIcon || 'ArrowLeftIcon',
        gotoNewTab: !!data.gotoNewTab,
        refresh: !!data.refresh,
        refreshLabel: data.refreshLabel || 'Refresh',
        // [{ key: 'totalRows', icon: 'UsersIcon', label: 'Total Engineers', value: 267 }, …]
        // copies are built so the shared data file is never mutated
        stats: (Array.isArray(data.stats) ? data.stats : []).map(stat => {
          const live = stat.key ? props.statsValues[stat.key] : undefined
          return { ...stat, value: live !== undefined && live !== null ? live : stat.value }
        }),
      }
    })

    // goto: 'https://…' -> external link, opened in a NEW TAB
    //       'analysis'  -> route name, '/analysis' -> path, { name, params, query } -> as is
    //       gotoNewTab: true in the header data opens an internal route in a new tab as well
    const goTo = () => {
      const { goto, gotoNewTab } = header.value
      if (!goto) return

      // external link (http, https, protocol-relative, mailto, tel) -> new tab
      if (typeof goto === 'string' && EXTERNAL_LINK.test(goto)) {
        // "noopener,noreferrer" keeps the opened page from controlling this one
        window.open(goto, '_blank', 'noopener,noreferrer')
        return
      }

      let location = goto
      if (typeof goto === 'string') {
        location = goto.startsWith('/') ? { path: goto } : { name: goto }
      }

      // internal route that should still open in a new tab
      if (gotoNewTab) {
        window.open(router.resolve(location).href, '_blank', 'noopener,noreferrer')
        return
      }

      // ignore "navigating to the page you are already on"
      router.push(location).catch(() => {})
    }

    return {
      header,
      goTo,
    }
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/pages/main-header-page/main-header-page.scss';
</style>
